/* =============================================================================
 * Auth event gate
 * -----------------------------------------------------------------------------
 * Its own module, with no imports, deliberately.
 *
 * This lives apart from session.tsx because that file constructs the Supabase
 * client at import time and throws without credentials — which would make a
 * pure decision function untestable anywhere the environment is not configured,
 * CI included. There is nothing here but the rule.
 * ========================================================================== */

/**
 * Decides whether an auth event should trigger a full workspace reload.
 *
 * `onAuthStateChange` fires on every token refresh and every return from the
 * background. Reloading on all of them throws the user back to the boot screen
 * mid-page; reloading too rarely strands the app on that screen for ever.
 * Both have happened, so the rule is written down once and tested.
 *
 * The invariant everything rests on: `loading` is only ever cleared by a load,
 * so **the first event must always load**, including when the answer is
 * "nobody is signed in".
 *
 * It is a factory rather than a bare function because the bug was never in the
 * comparison — it was the starting value. Tracking "not loaded yet" as `null`
 * made it indistinguishable from the real `null` meaning "signed out", so the
 * first event on a fresh install was dismissed as a no-op and the boot screen
 * never went away. Owning that value in here means no caller can supply it,
 * and therefore no caller can get it wrong.
 */
export function createAuthGate(): (userId: string | null) => boolean {
  // The one piece of state, and the reason this is a closure. `NOT_LOADED` is
  // a symbol so that it cannot collide with any user id or with `null`.
  const NOT_LOADED = Symbol('not-loaded');
  let loadedFor: string | null | typeof NOT_LOADED = NOT_LOADED;

  return function shouldReload(userId: string | null): boolean {
    if (loadedFor === userId) return false;
    loadedFor = userId;
    return true;
  };
}
